export const BRIGHTNESS = {
  LIGHT: 'light',
  DARK: 'dark',
} as const

export type BrightnessType = (typeof BRIGHTNESS)[keyof typeof BRIGHTNESS]

export interface RGB {
  r: number
  g: number
  b: number
}

export interface HSL {
  h: number
  s: number
  l: number
}

export function hexToRGB(hex: string): RGB {
  hex = hex.replace('#', '')
  const aRgbHex = hex.match(/.{1,2}/g)

  if (!aRgbHex || aRgbHex.length < 3) {
    // Return black as fallback for invalid hex colors
    return { r: 0, g: 0, b: 0 }
  }

  return {
    r: parseInt(aRgbHex[0], 16),
    g: parseInt(aRgbHex[1], 16),
    b: parseInt(aRgbHex[2], 16),
  }
}

export function computeLuminance(color: RGB): number {
  // Formula: http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
  const colorArray = [color.r, color.g, color.b]
  for (let i = 0; i < 3; i++) {
    let rgb = colorArray[i]
    rgb /= 255
    rgb = rgb < 0.03928 ? rgb / 12.92 : Math.pow((rgb + 0.055) / 1.055, 2.4)
    colorArray[i] = rgb
  }
  return 0.2126 * colorArray[0] + 0.7152 * colorArray[1] + 0.0722 * colorArray[2]
}

export function estimateBrightnessForColor(colorHex: string): BrightnessType {
  // See <https://www.w3.org/TR/WCAG20/#contrast-ratiodef>
  // Adapted from https://api.flutter.dev/flutter/material/ThemeData/estimateBrightnessForColor.html
  const rgb = hexToRGB(colorHex)
  const luminance = computeLuminance(rgb)
  const kThreshold = 0.15
  return (luminance + 0.05) * (luminance + 0.05) > kThreshold ? BRIGHTNESS.LIGHT : BRIGHTNESS.DARK
}

export function hexToHSL(hex: string): HSL {
  const rgb = hexToRGB(hex)

  let r = rgb.r / 255
  let g = rgb.g / 255
  let b = rgb.b / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h, s, l }
}

export function componentToHex(c: number): string {
  const hex = Math.floor(c).toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

/**
 * https://axonflux.com/handy-rgb-to-hsl-and-rgb-to-hsv-color-model-c
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 */
function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1
  if (t > 1) t -= 1
  if (t < 1 / 6) return p + (q - p) * 6 * t
  if (t < 1 / 2) return q
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
  return p
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number

  if (s === 0) {
    r = g = b = l // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return [r * 255, g * 255, b * 255]
}

export function randomColor(): string {
  const rgb = hslToRgb(Math.random(), 0.6, Math.random() * 0.4 + 0.4)
  return `#${componentToHex(rgb[0])}${componentToHex(rgb[1])}${componentToHex(rgb[2])}`
}
